const initYogItRecipes = (section) => {
  if (!section || section.dataset.sealYogRecipesReady === 'true') return;
  section.dataset.sealYogRecipesReady = 'true';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const reveals = [...section.querySelectorAll('[data-yog-recipe-reveal]')];

  if (reduceMotion || !('IntersectionObserver' in window)) {
    reveals.forEach((element) => element.classList.add('is-visible'));
  } else {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    reveals.forEach((element) => observer.observe(element));
  }

  const tabs = [...section.querySelectorAll('[data-yog-recipe-category]')];
  const panels = [...section.querySelectorAll('[data-yog-recipe-panel]')];
  const panelControllers = new Map();

  const initPanel = (panel) => {
    const track = panel.querySelector('[data-yog-recipe-track]');
    const recipes = [...panel.querySelectorAll('[data-yog-recipe]')];
    const previousButton = panel.querySelector('[data-yog-recipe-previous]');
    const nextButton = panel.querySelector('[data-yog-recipe-next]');
    const progressMarkers = [...panel.querySelectorAll('[data-yog-recipe-progress]')];
    const status = panel.querySelector('[data-yog-recipe-status]');

    if (!track || recipes.length < 2 || !previousButton || !nextButton) {
      return { reset: () => {} };
    }

    let currentIndex = 0;
    let scrollFrame;
    let resizeFrame;

    const getVisibleRecipeCount = () => {
      const trackWidth = track.clientWidth;
      const recipeWidth = recipes[0]?.getBoundingClientRect().width || 0;
      if (!trackWidth || !recipeWidth) return 1;

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
      return Math.max(1, Math.min(recipes.length, Math.round((trackWidth + gap) / (recipeWidth + gap))));
    };

    const getMaximumIndex = () => Math.max(0, recipes.length - getVisibleRecipeCount());

    const updateNavigation = (nextIndex) => {
      const visibleRecipeCount = getVisibleRecipeCount();
      const maximumIndex = Math.max(0, recipes.length - visibleRecipeCount);
      currentIndex = Math.max(0, Math.min(nextIndex, maximumIndex));
      previousButton.disabled = currentIndex === 0;
      nextButton.disabled = currentIndex === maximumIndex;
      progressMarkers.forEach((marker, index) => {
        marker.classList.toggle(
          'is-active',
          index >= currentIndex && index < currentIndex + visibleRecipeCount
        );
      });

      if (status) {
        const template = status.dataset.statusTemplate || '';
        status.textContent = template
          .replace('__current__', String(currentIndex + 1))
          .replace('__total__', String(recipes.length));
      }
    };

    const getNearestRecipeIndex = () => {
      const trackLeft = track.getBoundingClientRect().left;
      return recipes.reduce((nearestIndex, recipe, index) => {
        const nearestDistance = Math.abs(recipes[nearestIndex].getBoundingClientRect().left - trackLeft);
        const distance = Math.abs(recipe.getBoundingClientRect().left - trackLeft);
        return distance < nearestDistance ? index : nearestIndex;
      }, 0);
    };

    const scrollToRecipe = (nextIndex) => {
      const targetIndex = Math.max(0, Math.min(nextIndex, getMaximumIndex()));
      const trackRect = track.getBoundingClientRect();
      const recipeRect = recipes[targetIndex].getBoundingClientRect();
      const left = track.scrollLeft + recipeRect.left - trackRect.left;
      track.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      updateNavigation(targetIndex);
    };

    const reset = () => {
      track.scrollLeft = 0;
      updateNavigation(0);
    };

    previousButton.addEventListener('click', () => scrollToRecipe(currentIndex - 1));
    nextButton.addEventListener('click', () => scrollToRecipe(currentIndex + 1));
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => updateNavigation(getNearestRecipeIndex()));
      },
      { passive: true }
    );

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(() => {
          const nextIndex = Math.min(currentIndex, getMaximumIndex());
          const trackRect = track.getBoundingClientRect();
          const recipeRect = recipes[nextIndex].getBoundingClientRect();
          track.scrollLeft += recipeRect.left - trackRect.left;
          updateNavigation(nextIndex);
        });
      });
      resizeObserver.observe(track);
    }

    updateNavigation(0);
    return { reset };
  };

  panels.forEach((panel) => {
    panelControllers.set(panel.dataset.yogRecipePanel, initPanel(panel));
  });

  const activateCategory = (category, moveFocus = false) => {
    const activeTab = tabs.find((tab) => tab.dataset.yogRecipeCategory === category);
    const activePanel = panels.find((panel) => panel.dataset.yogRecipePanel === category);
    if (!activeTab || !activePanel) return;

    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      panel.hidden = panel !== activePanel;
    });

    requestAnimationFrame(() => {
      panelControllers.get(category)?.reset();
      activePanel.querySelectorAll('[data-yog-recipe-reveal]').forEach((element) => {
        element.classList.add('is-visible');
      });
    });

    if (moveFocus) activeTab.focus();
  };

  tabs.forEach((tab, tabIndex) => {
    tab.addEventListener('click', () => activateCategory(tab.dataset.yogRecipeCategory));
    tab.addEventListener('keydown', (event) => {
      let nextIndex = tabIndex;

      if (event.key === 'ArrowRight') nextIndex = (tabIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (tabIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === tabIndex) return;

      event.preventDefault();
      activateCategory(tabs[nextIndex].dataset.yogRecipeCategory, true);
    });
  });
};

const initAllYogItRecipes = () => {
  document.querySelectorAll('[data-seal-yog-it-recipes]').forEach(initYogItRecipes);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllYogItRecipes);
} else {
  initAllYogItRecipes();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target?.querySelector?.('[data-seal-yog-it-recipes]');
  if (!section) return;
  section.dataset.sealYogRecipesReady = 'false';
  initYogItRecipes(section);
});
